import React, { useState, useEffect } from 'react';
import { PackageSearch } from 'lucide-react';
import ScrollReveal from '../common/ScrollReveal';
import CategoryCarousel from '../product/CategoryCarousel';
import { getCategories } from '../../data/storeData';

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 rounded-2xl bg-white border border-gray-200/70 shadow-sm flex items-center justify-center mb-5">
      <PackageSearch className="w-7 h-7 text-gray-300" strokeWidth={1.5} />
    </div>
    <p className="text-gray-500 font-medium">{message}</p>
  </div>
);

const CatalogSection = ({ activeCategory, onAddToCart, products }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();

    const handleUpdate = () => loadCategories();
    window.addEventListener('categoriesUpdated', handleUpdate);
    window.addEventListener('productsUpdated', handleUpdate);

    return () => {
      window.removeEventListener('categoriesUpdated', handleUpdate);
      window.removeEventListener('productsUpdated', handleUpdate);
    };
  }, []);

  const renderContent = () => {
    if (loading || !categories || categories.length === 0 || !products) {
      return <EmptyState message="Loading products..." />;
    }

    // Show all categories
    if (activeCategory === 'all') {
      return (
        <div className="space-y-10 md:space-y-14">
          {categories.map((category) => {
            const categoryProducts = products.filter(p => p.categoryId === category.id);

            if (categoryProducts.length === 0) return null;

            return (
              <CategoryCarousel
                key={category.id}
                title={category.name}
                products={categoryProducts}
                onAddToCart={onAddToCart}
              />
            );
          })}
        </div>
      );
    }

    // Check if it's a main category
    const category = categories.find(c => c.id === activeCategory);
    if (category) {
      const categoryProducts = products.filter(p => p.categoryId === category.id);

      if (categoryProducts.length === 0) {
        return <EmptyState message="No products in this category yet." />;
      }

      return (
        <CategoryCarousel
          title={category.name}
          products={categoryProducts}
          onAddToCart={onAddToCart}
        />
      );
    }

    // Check if it's a subcategory
    let subcategoryProducts = [];
    let subcategoryName = '';

    for (const cat of categories) {
      if (cat.subcategories) {
        const sub = cat.subcategories.find(s => s.id === activeCategory);
        if (sub) {
          subcategoryProducts = products.filter(p => p.subcategoryId === activeCategory);
          subcategoryName = sub.name;
          break;
        }
      }
    }

    if (subcategoryProducts.length === 0) {
      return <EmptyState message="No products in this category yet." />;
    }

    return (
      <CategoryCarousel
        title={subcategoryName}
        products={subcategoryProducts}
        onAddToCart={onAddToCart}
      />
    );
  };

  return (
    <section className="bg-[#f5f5f7] py-20 md:py-28 min-h-screen relative" id="catalog">
      <div className="absolute inset-0 bg-gradient-to-b from-white to-[#f5f5f7] h-64 pointer-events-none"></div>
      <div className="relative z-10">
        <ScrollReveal>
          <div className="container mx-auto px-6 text-center mb-14 md:mb-20">
            <span className="inline-block text-orange-500 text-xs font-bold tracking-[0.25em] uppercase mb-4">
              Our Catalog
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-[#1d1d1f]">
              The latest.
              <br className="md:hidden" />
              <span className="text-[#86868b]"> Take a look at what's new.</span>
            </h2>
          </div>
        </ScrollReveal>

        {renderContent()}
      </div>
    </section>
  );
};

export default CatalogSection;
